/*
 * AssistantDrawer — the admin console assistant (NEXUS-1384, chat v2).
 *
 * Window: docked to the right edge (drag its left edge to resize) or floating (drag the header to
 * move, drag the edges / corner to resize). Layout and size are remembered per browser.
 *
 * Chat, following the K-Stack assistant style (Claude-app-like, see the Helm chat-style issue):
 * user turns are right-aligned bubbles, assistant turns are full-width text with a quiet footer
 * (time · model · first-token wait · generation time · tokens) and Copy / Retry. Reports the
 * model asks for render inline as chart + grid with Pin to dashboard and Download.
 *
 * Composer: multi-line, grows with the text and can be resized with its grip; Enter sends,
 * Shift+Enter is a new line. Text documents can be attached with +, pasted, or dropped on the
 * window; very long pastes become an attachment chip.
 *
 * History: every conversation is saved (aquastarAssistantChatSave). The clock button lists past
 * chats — admins see everyone's, with who asked — and reopens one; another person's chat opens
 * read-only with "Continue in a new chat". ⤓ exports the whole conversation, charts and grids
 * included, as a standalone HTML file.
 *
 * Read-only by design: the only tool is run_report; pins go through the staff-gated pin
 * functions, which validate the report id and params again.
 *
 * Wiki review markup (AQUASTAR-606): with a wiki role the Assistant also reads review markups
 * (list / get / suggested issues) and proposes issues or a completion; both writes run only when
 * the person confirms on the card (lib/assistantWiki, AssistantWikiCards).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { STAGE } from '../lib/config';
import { appRest, getFn } from '../lib/apiClient';
import { followUpsFor } from '../lib/assistantHelp';
import { HELP_PAGES, OpenInWiki, WikiHelpBody } from './WikiHelp';
import {
  buildSystemPrompt,
  ChatError,
  FALLBACK_CATALOGUE,
  ISSUES_CLARIFY,
  needsIssuesClarification,
  withClientReports,
  parseToolCall,
  streamChatEx,
  visibleText,
  type ChatTurn,
  type ReportCatalogueEntry,
} from '../lib/assistantChat';
import { addPin, isPinnable, reportDigest, runReport, type ReportResult } from '../lib/reports';
import {
  buildChatHtml,
  composeUserContent,
  deleteChat,
  fmtClock,
  fmtMs,
  forViewer,
  listChats,
  loadChat,
  pastedAttachment,
  readAttachment,
  saveChat,
  statsLine,
  titleFrom,
  TOOL_RESULT_NUDGE,
  WIKI_RESULT_NUDGE,
  wikiActionResult,
  turnsFromMessages,
  type Attachment,
  type ChatSummary,
  type UiMessage,
} from '../lib/assistantStore';
import { Markdown } from '../lib/markdown';
import { chartSvgFrom, downloadHtml, slugify } from '../lib/exportHtml';
import ReportView, { type ReportViewHandle } from './ReportView';
import { WikiActionCard, WikiResultCard } from './AssistantWikiCards';
import {
  digestList,
  digestMarkup,
  digestProposals,
  draftIssues,
  fetchWikiMe,
  getMarkup,
  isWikiWrite,
  listMarkups,
  pickVersion,
  type WikiMeLite,
} from '../lib/assistantWiki';

const SUGGESTIONS = [
  'Which pumps had faults in the last 30 days, and how many each?',
  'What are the most common fault codes this month?',
  'Show me the fault trend for the last 90 days',
  'Who controlled pumps the most this month?',
  'Which faults are still open right now?',
];

const MAX_TOOL_ROUNDS = 2;
/** Reading markups takes a few steps (list → get → suggested issues → proposal). */
const MAX_WIKI_TOOL_ROUNDS = 4;
const LONG_PASTE_CHARS = 3000;
const LAYOUT_KEY = 'aqua.assistant.window';
const COMPOSER_KEY = 'aqua.assistant.composerH';

/** Last n turns, starting on a user turn (some providers reject a leading assistant turn). */
function tailFromUser(h: ChatTurn[], n: number): ChatTurn[] {
  let t = h.slice(-n);
  while (t.length > 1 && t[0]!.role !== 'user') t = t.slice(1);
  return t;
}

let seq = 0;
const nextId = () => `m${Date.now().toString(36)}${(seq++).toString(36)}`;
const nowIso = () => new Date().toISOString();

function pageName(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  const first = parts[0] ?? 'dashboard';
  if (first === 'platform') return 'platform';
  return first.replace(/-/g, ' ');
}

// ── Window geometry ─────────────────────────────────────────────────────

interface Geometry {
  mode: 'docked' | 'floating';
  dockW: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

const MIN_W = 360;
const MIN_H = 420;

function defaultGeometry(): Geometry {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const w = Math.min(560, vw - 40);
  const h = Math.min(720, vh - 120);
  return { mode: 'docked', dockW: Math.min(560, vw), x: Math.max(20, vw - w - 40), y: 80, w, h };
}

function loadGeometry(): Geometry {
  const d = defaultGeometry();
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Geometry>;
      const g: Geometry = { ...d };
      for (const k of ['dockW', 'x', 'y', 'w', 'h'] as const) if (Number.isFinite(p[k])) g[k] = p[k] as number;
      if (p.mode === 'floating' || p.mode === 'docked') g.mode = p.mode;
      return g;
    }
  } catch {
    /* per-browser convenience only */
  }
  return d;
}

function clampGeometry(g: Geometry): Geometry {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.max(MIN_W, Math.min(g.w, vw - 16));
  const h = Math.max(MIN_H, Math.min(g.h, vh - 16));
  return {
    ...g,
    dockW: Math.max(MIN_W, Math.min(g.dockW, Math.max(MIN_W, vw - 80))),
    w,
    h,
    x: Math.max(8 - w + 120, Math.min(g.x, vw - 120)),
    y: Math.max(0, Math.min(g.y, vh - 48)),
  };
}

/** Pointer-drag helper: calls onMove(dx, dy) relative to the press point until release. */
function startDrag(e: React.PointerEvent, onMove: (dx: number, dy: number) => void, onEnd?: () => void) {
  e.preventDefault();
  const sx = e.clientX;
  const sy = e.clientY;
  const prevSelect = document.body.style.userSelect;
  document.body.style.userSelect = 'none';
  let done = false;
  const up = () => {
    if (done) return;
    done = true;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
    window.removeEventListener('blur', up);
    document.body.style.userSelect = prevSelect;
    onEnd?.();
  };
  const move = (ev: PointerEvent) => {
    if (ev.pointerType === 'mouse' && ev.buttons === 0) return up();
    onMove(ev.clientX - sx, ev.clientY - sy);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
  window.addEventListener('blur', up);
}

// ── Icons ───────────────────────────────────────────────────────────────

const I = {
  info: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <circle cx="8" cy="8" r="6.3" />
      <path d="M8 7.2v4M8 4.9v.1" />
    </svg>
  ),
  history: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2.5 8a5.5 5.5 0 1 0 1.6-3.9" />
      <path d="M2 2.5v2.8h2.8M8 5v3.2l2 1.3" />
    </svg>
  ),
  newChat: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13 8.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4.5" />
      <path d="M11.5 1.8l2.7 2.7L8 10.7H5.3V8z" />
    </svg>
  ),
  download: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 2v8M4.5 7 8 10.5 11.5 7M2.5 13.5h11" />
    </svg>
  ),
  float: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden>
      <rect x="1.5" y="3.5" width="13" height="10" rx="1.5" />
      <rect x="6" y="6" width="6.5" height="5" rx="1" fill="currentColor" opacity=".25" />
    </svg>
  ),
  dock: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden>
      <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
      <path d="M10 2.5v11" />
      <rect x="10" y="2.5" width="4.5" height="11" fill="currentColor" opacity=".25" />
    </svg>
  ),
  close: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <path d="M3 3l10 10M13 3 3 13" />
    </svg>
  ),
  copy: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden>
      <rect x="5" y="5" width="9" height="9" rx="1.5" />
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
    </svg>
  ),
  retry: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
      <path d="M12.5 1.5v3h-3" strokeLinejoin="round" />
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <path d="M8 3v10M3 8h10" />
    </svg>
  ),
  send: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" />
    </svg>
  ),
  stop: (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
      <rect x="1" y="1" width="10" height="10" rx="2" fill="currentColor" />
    </svg>
  ),
  trash: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      <path d="M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.5h6.6L12 4" />
    </svg>
  ),
};

function HeadButton({ label, onClick, children, active, disabled, testId }: { label: string; onClick: () => void; children: ReactNode; active?: boolean; disabled?: boolean; testId?: string }) {
  return (
    <button
      type="button"
      className="btn btn-sm d-inline-flex align-items-center justify-content-center"
      style={{
        width: 30,
        height: 30,
        padding: 0,
        color: 'var(--aqua-text-on-dark)',
        background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
        border: '1px solid rgba(255,255,255,0.18)',
      }}
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

// ── Message pieces ──────────────────────────────────────────────────────

function PinButton({ result }: { result: ReportResult }) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState<string | null>(null);
  const pin = async () => {
    setState('busy');
    setMsg(null);
    try {
      const days = result.params.days;
      const r = await addPin({
        report: result.report,
        params: result.params,
        title: days ? `${result.title} · last ${days} days` : result.title,
        chartType: result.chart ? result.chart.type : 'table',
      });
      setState('done');
      setMsg(r.existed ? 'Already on the dashboard' : 'Pinned to the dashboard');
    } catch (e) {
      setState('error');
      setMsg(e instanceof Error ? e.message : 'Could not pin');
    }
  };
  if (state === 'done') {
    return (
      <span className="small text-success" data-testid="pin-done">
        ✓ {msg} · <Link to="/dashboard">Open dashboard</Link>
      </span>
    );
  }
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={pin} disabled={state === 'busy'} data-testid="pin-report">
        📌 {state === 'busy' ? 'Pinning…' : 'Pin to dashboard'}
      </button>
      {state === 'error' && <span className="small text-danger">{msg}</span>}
    </span>
  );
}

function ReportBubble({ msg, readOnly }: { msg: Extract<UiMessage, { kind: 'report' }>; readOnly: boolean }) {
  const result = msg.result;
  const [mode, setMode] = useState<'both' | 'table' | 'chart'>(result.chart ? 'both' : 'table');
  const handle = useRef<ReportViewHandle | null>(null);
  return (
    <div className="card border-0 my-2" style={{ boxShadow: 'var(--aqua-shadow-sm)' }} data-report-msg={msg.id}>
      <div className="card-body p-3">
        <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
          <div style={{ minWidth: 0 }}>
            <div className="fw-bold small" style={{ color: 'var(--aqua-text-strong)' }}>
              {result.title}
            </div>
            {result.subtitle && (
              <div className="text-muted" style={{ fontSize: 12 }}>
                {result.subtitle}
              </div>
            )}
          </div>
          {result.chart && (
            <div className="btn-group btn-group-sm flex-shrink-0" role="group" aria-label="View">
              {(['both', 'chart', 'table'] as const).map((m) => (
                <button key={m} type="button" className={`btn ${mode === m ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setMode(m)} style={{ fontSize: 11 }}>
                  {m === 'both' ? 'Both' : m === 'chart' ? 'Chart' : 'Grid'}
                </button>
              ))}
            </div>
          )}
        </div>
        <ReportView result={result} mode={mode} compact maxRows={8} handleRef={handle} />
        <details className="mt-2" data-testid="tool-step">
          <summary className="text-muted" style={{ fontSize: 11, cursor: 'pointer' }}>
            🔧 Ran “{result.title}”
            {Object.entries(result.params || {})
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([k, v]) => ` · ${k} ${String(v)}`)
              .join('')}
            {msg.ms ? ` · ${fmtMs(msg.ms)}` : ''} · {result.rows.length} row{result.rows.length === 1 ? '' : 's'}
          </summary>
          <pre className="small mb-0 mt-1 p-2 rounded" style={{ background: 'rgba(16,28,66,0.05)', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify({ tool: 'run_report', report: result.report, args: msg.args ?? result.params }, null, 2)}
          </pre>
        </details>
        <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
          {!readOnly && isPinnable(result.report) && <PinButton result={result} />}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handle.current?.exportHtml({ title: result.title })}>
            ⤓ Download
          </button>
          <span className="text-muted ms-auto" style={{ fontSize: 11 }}>
            {fmtClock(msg.at)}
            {msg.ms ? ` · ran in ${fmtMs(msg.ms)}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

function Elapsed({ since }: { since: number }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 200);
    return () => clearInterval(t);
  }, []);
  return <span>{((Date.now() - since) / 1000).toFixed(1)} s</span>;
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 text-muted text-decoration-none d-inline-flex align-items-center gap-1"
      style={{ fontSize: 11 }}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
      title="Copy"
    >
      {I.copy} {done ? 'Copied' : 'Copy'}
    </button>
  );
}

// ── Proposed action (create issue) ──────────────────────────────────────

interface IssueMetaLite {
  types: Array<{ key: string; name?: string }>;
  severities: Array<{ key: string; name?: string; label?: string }>;
}

function ActionCard({
  msg,
  readOnly,
  onUpdate,
}: {
  msg: Extract<UiMessage, { kind: 'action' }>;
  readOnly: boolean;
  onUpdate: (patch: Partial<Extract<UiMessage, { kind: 'action' }>>) => void;
}) {
  const [title, setTitle] = useState(msg.title);
  const [description, setDescription] = useState(msg.description);
  const [meta, setMeta] = useState<IssueMetaLite | null>(null);
  const [typeKey, setTypeKey] = useState('');
  const [severityKey, setSeverityKey] = useState(msg.severity ?? '');
  const open = msg.status === 'proposed' || msg.status === 'error';

  useEffect(() => {
    if (!open || readOnly) return;
    let cancelled = false;
    appRest<IssueMetaLite>('GET', '/issues/meta')
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        const pick = m.types.find((t) => /bug|support|issue|task/i.test(`${t.key} ${t.name ?? ''}`)) ?? m.types[0];
        setTypeKey((k) => k || pick?.key || '');
        if (msg.severity && !m.severities.some((x) => x.key === msg.severity)) setSeverityKey('');
      })
      .catch(() => setMeta({ types: [], severities: [] }));
    return () => {
      cancelled = true;
    };
  }, [open, readOnly, msg.severity]);

  const create = async () => {
    if (!title.trim() || !typeKey) return;
    onUpdate({ status: 'creating', title, description });
    try {
      const issue = await appRest<{ id: string; issueNumber?: number; number?: number; title?: string }>('POST', '/issues', {
        typeKey,
        title: title.trim(),
        description: `${description.trim()}\n\n_Filed from the admin assistant._`,
        severityKey: severityKey || undefined,
        idempotencyKey: `assistant-${msg.id}`,
      });
      onUpdate({ status: 'created', issueId: issue.id, issueNumber: issue.issueNumber ?? issue.number ?? null, title: issue.title || title, description });
    } catch (e) {
      onUpdate({ status: 'error', error: e instanceof Error ? e.message : 'Could not create the issue' });
    }
  };

  return (
    <div className="card border-0 my-2" style={{ boxShadow: 'var(--aqua-shadow-sm)', borderLeft: '3px solid var(--aqua-primary)' }} data-testid="action-card">
      <div className="card-body p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="fw-bold small">🗂 Create an issue</span>
          <span className={`badge ms-auto ${msg.status === 'created' ? 'text-bg-success' : msg.status === 'cancelled' ? 'text-bg-secondary' : msg.status === 'error' ? 'text-bg-danger' : 'text-bg-warning'}`}>
            {msg.status === 'proposed' ? 'Needs your OK' : msg.status === 'creating' ? 'Creating…' : msg.status === 'created' ? 'Created' : msg.status === 'cancelled' ? 'Cancelled' : 'Failed'}
          </span>
        </div>
        {open && !readOnly ? (
          <>
            <input className="form-control form-control-sm mb-2" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Issue title" maxLength={140} data-testid="action-title" />
            <textarea className="form-control form-control-sm mb-2" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Issue description" />
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={typeKey} onChange={(e) => setTypeKey(e.target.value)} aria-label="Issue type">
                {(meta?.types ?? []).map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name || t.key}
                  </option>
                ))}
              </select>
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={severityKey} onChange={(e) => setSeverityKey(e.target.value)} aria-label="Severity">
                <option value="">Default severity</option>
                {(meta?.severities ?? []).map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.name || x.label || x.key}
                  </option>
                ))}
              </select>
              <span className="ms-auto d-inline-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onUpdate({ status: 'cancelled' })} data-testid="action-cancel">
                  Cancel
                </button>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => void create()} disabled={!title.trim() || !typeKey} data-testid="action-confirm">
                  Create issue
                </button>
              </span>
            </div>
            {msg.status === 'error' && <div className="text-danger small mt-2">{msg.error}</div>}
            <div className="text-muted mt-2" style={{ fontSize: 11 }}>
              Nothing is created until you press Create issue. It will be filed under your name.
            </div>
          </>
        ) : (
          <>
            <div className="small fw-semibold">{msg.title}</div>
            {msg.description && (
              <div className="small text-muted mt-1">
                <Markdown text={msg.description} />
              </div>
            )}
            {msg.status === 'created' && msg.issueId && (
              <Link to={`/issues/${encodeURIComponent(msg.issueId)}`} className="small">
                Open issue{msg.issueNumber ? ` #${msg.issueNumber}` : ''} →
              </Link>
            )}
            {open && readOnly && <div className="text-muted small">Not created.</div>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Help panel ──────────────────────────────────────────────────────────

function HelpPanel({ onClose, onUse }: { onClose: () => void; onUse: (prompt: string) => void }) {
  // The content is the wiki page User guide › Admin console › Admin assistant (one source of
  // truth, Erwin 2026-10-27); its example prompts are `[prompt](#ask)` links → onUse.
  return (
    <div className="position-absolute d-flex flex-column" style={{ inset: 0, background: 'var(--aqua-surface)', zIndex: 5 }} data-testid="assistant-help">
      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
        <div className="fw-bold flex-grow-1">Using the assistant</div>
        <OpenInWiki slug={HELP_PAGES.assistant} />
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Back to chat
        </button>
      </div>
      <div className="flex-grow-1 px-3 py-2" style={{ overflowY: 'auto', minHeight: 0 }}>
        <WikiHelpBody slug={HELP_PAGES.assistant} onAsk={onUse} />
      </div>
    </div>
  );
}

// ── History panel ───────────────────────────────────────────────────────

function groupLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t = d.getTime();
  if (t >= today.getTime()) return 'Today';
  if (t >= today.getTime() - 86_400_000) return 'Yesterday';
  if (t >= today.getTime() - 6 * 86_400_000) return 'This week';
  return 'Older';
}

function HistoryPanel({
  activeId,
  onOpen,
  onClose,
  onDeleted,
}: {
  activeId: string | null;
  onOpen: (id: string) => void;
  onClose: () => void;
  onDeleted: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'all' | 'mine'>('all');
  const [data, setData] = useState<{ chats: ChatSummary[]; canSeeAll: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      listChats({ q, scope })
        .then((r) => {
          if (!cancelled) {
            setData(r);
            setError(null);
          }
        })
        .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Could not load history'));
    }, q ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, scope]);

  const groups = useMemo(() => {
    const out: Array<[string, ChatSummary[]]> = [];
    for (const c of data?.chats ?? []) {
      const g = groupLabel(c.updatedAt);
      const last = out[out.length - 1];
      if (last && last[0] === g) last[1].push(c);
      else out.push([g, [c]]);
    }
    return out;
  }, [data]);

  const remove = async (c: ChatSummary) => {
    if (!window.confirm(`Delete “${c.title || 'this chat'}”?`)) return;
    try {
      await deleteChat(c.id);
      setData((d) => (d ? { ...d, chats: d.chats.filter((x) => x.id !== c.id) } : d));
      onDeleted(c.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete');
    }
  };

  return (
    <div
      className="position-absolute d-flex flex-column"
      style={{ inset: 0, background: 'var(--aqua-surface)', zIndex: 5 }}
      data-testid="assistant-history"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
        <div className="fw-bold flex-grow-1">Chat history</div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Back to chat
        </button>
      </div>
      <div className="px-3 py-2 d-flex gap-2 align-items-center">
        <input className="form-control form-control-sm" placeholder="Search titles, people and messages…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus aria-label="Search history" />
        {data?.canSeeAll && (
          <div className="btn-group btn-group-sm flex-shrink-0" role="group" aria-label="Whose chats">
            <button type="button" className={`btn ${scope === 'all' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setScope('all')}>
              Everyone
            </button>
            <button type="button" className={`btn ${scope === 'mine' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setScope('mine')}>
              Mine
            </button>
          </div>
        )}
      </div>
      <div className="flex-grow-1 px-2 pb-3" style={{ overflowY: 'auto', minHeight: 0 }}>
        {error && <div className="alert alert-warning small mx-1">{error}</div>}
        {!data && !error && <div className="text-muted small px-2 py-4 text-center">Loading…</div>}
        {data && data.chats.length === 0 && <div className="text-muted small px-2 py-4 text-center">{q ? 'No chats match.' : 'No saved chats yet.'}</div>}
        {groups.map(([g, chats]) => (
          <div key={g}>
            <div className="text-uppercase text-muted fw-semibold px-2 mt-2 mb-1" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
              {g}
            </div>
            {chats.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(c.id)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(c.id);
                  }
                }}
                className="d-flex align-items-start gap-2 px-2 py-2 rounded aqua-history-row"
                style={{ cursor: 'pointer', background: c.id === activeId ? 'rgba(1,157,220,0.10)' : undefined }}
                data-testid="history-row"
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold flex-shrink-0"
                  style={{ width: 26, height: 26, fontSize: 10, background: c.mine ? 'var(--aqua-primary)' : '#8f5bd6', marginTop: 2 }}
                  aria-hidden
                >
                  {(c.userName || '?').split(/[\s.@]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('')}
                </span>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="text-truncate small fw-semibold">{c.title || 'Untitled chat'}</div>
                  <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                    {c.mine ? 'You' : c.userName || 'Unknown'}
                    {c.userRole ? ` (${c.userRole})` : ''} · {c.messageCount} message{c.messageCount === 1 ? '' : 's'}
                    {c.page ? ` · on ${c.page}` : ''} ·{' '}
                    {new Date(c.updatedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
                {(c.mine || data?.canSeeAll) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-muted p-1"
                    title="Delete chat"
                    aria-label="Delete chat"
                    onClick={(e) => {
                      e.stopPropagation();
                      void remove(c);
                    }}
                  >
                    {I.trash}
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Composer ────────────────────────────────────────────────────────────

function Composer({
  busy,
  disabled,
  onSend,
  onStop,
  attachments,
  setAttachments,
  onError,
  prefill,
  contextLabel,
  onClearContext,
}: {
  busy: boolean;
  disabled: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  attachments: Attachment[];
  setAttachments: (fn: (a: Attachment[]) => Attachment[]) => void;
  onError: (msg: string) => void;
  prefill?: { text: string; n: number } | null;
  contextLabel?: string | null;
  onClearContext?: () => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text);
    setTimeout(() => ta.current?.focus(), 0);
  }, [prefill]);
  const ta = useRef<HTMLTextAreaElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const pasteN = useRef(1);
  // Manual height from the grip (px). null = auto-grow up to AUTO_MAX.
  const [manualH, setManualH] = useState<number | null>(() => {
    try {
      const v = Number(localStorage.getItem(COMPOSER_KEY));
      return Number.isFinite(v) && v >= 40 ? v : null;
    } catch {
      return null;
    }
  });
  const AUTO_MAX = 220;

  useEffect(() => {
    const el = ta.current;
    if (!el || manualH !== null) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(AUTO_MAX, Math.max(44, el.scrollHeight))}px`;
  }, [text, manualH]);

  const addFiles = async (files: FileList | File[]) => {
    for (const f of Array.from(files)) {
      try {
        const a = await readAttachment(f);
        setAttachments((xs) => [...xs, a]);
      } catch (e) {
        onError(e instanceof Error ? e.message : `Could not read ${f.name}`);
      }
    }
  };

  const submit = () => {
    if (busy || disabled) return;
    if (!text.trim() && attachments.length === 0) return;
    onSend(text);
    setText('');
  };

  return (
    <form
      className="px-3 pb-3 pt-2"
      style={{ flex: '0 0 auto', background: 'var(--aqua-surface-muted)' }}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="bg-white rounded-4 border" style={{ boxShadow: 'var(--aqua-shadow-md)' }}>
        {/* Grip: drag up/down to give the prompt more or less room; double-click to reset. */}
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize the message box"
          title="Drag to resize · double-click to reset"
          onPointerDown={(e) => {
            const start = ta.current?.offsetHeight ?? 44;
            const frame = ta.current?.closest('aside') as HTMLElement | null;
            const cap = Math.round((frame?.clientHeight ?? window.innerHeight) * 0.45);
            startDrag(
              e,
              (_dx, dy) => {
                const h = Math.max(40, Math.min(cap, start - dy));
                setManualH(h);
              },
              () => {
                try {
                  if (ta.current) localStorage.setItem(COMPOSER_KEY, String(ta.current.offsetHeight));
                } catch {
                  /* convenience */
                }
              },
            );
          }}
          onDoubleClick={() => {
            setManualH(null);
            try {
              localStorage.removeItem(COMPOSER_KEY);
            } catch {
              /* convenience */
            }
          }}
          style={{ height: 10, cursor: 'ns-resize', display: 'flex', justifyContent: 'center', alignItems: 'center', touchAction: 'none' }}
          data-testid="composer-grip"
        >
          <span style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(16,28,66,0.15)' }} />
        </div>
        {contextLabel && (
          <div className="px-2 pb-1">
            <span className="badge rounded-pill fw-normal d-inline-flex align-items-center gap-1" style={{ background: 'rgba(1,157,220,0.12)', color: 'var(--aqua-text)', fontSize: 12 }} data-testid="context-chip" title="The assistant knows you mean this pump">
              📍 {contextLabel}
              <button type="button" className="btn-close" style={{ fontSize: 8 }} aria-label="Ask about the whole fleet instead" onClick={onClearContext} />
            </span>
          </div>
        )}
        {attachments.length > 0 && (
          <div className="d-flex flex-wrap gap-1 px-2 pb-1" data-testid="attachment-chips">
            {attachments.map((a) => (
              <span key={a.id} className="badge rounded-pill text-bg-light border fw-normal d-inline-flex align-items-center gap-1" style={{ fontSize: 12 }} title={`${a.chars.toLocaleString()} characters`}>
                📄 {a.name} · {(a.size / 1024).toFixed(1)} KB
                <button type="button" className="btn-close" style={{ fontSize: 8 }} aria-label={`Remove ${a.name}`} onClick={() => setAttachments((xs) => xs.filter((x) => x.id !== a.id))} />
              </span>
            ))}
          </div>
        )}
        <textarea
          ref={ta}
          className="form-control border-0 shadow-none px-3"
          style={{ resize: 'none', height: manualH ?? undefined, minHeight: 40, maxHeight: manualH ? '45vh' : AUTO_MAX, overflowY: 'auto', background: 'transparent' }}
          rows={1}
          placeholder={disabled ? 'This chat is read-only' : 'Ask about pumps, faults, trends, people… (Shift+Enter for a new line)'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          onPaste={(e) => {
            const files = e.clipboardData?.files;
            if (files && files.length > 0) {
              e.preventDefault();
              void addFiles(files);
              return;
            }
            const pasted = e.clipboardData?.getData('text') ?? '';
            if (pasted.length > LONG_PASTE_CHARS) {
              e.preventDefault();
              setAttachments((xs) => [...xs, pastedAttachment(pasted, pasteN.current++)]);
            }
          }}
          disabled={disabled}
          aria-label="Message"
          data-testid="assistant-input"
        />
        <div className="d-flex align-items-center gap-2 px-2 pb-2">
          <button
            type="button"
            className="btn btn-sm btn-light rounded-circle d-inline-flex align-items-center justify-content-center"
            style={{ width: 32, height: 32, padding: 0 }}
            onClick={() => fileInput.current?.click()}
            title="Attach documents (txt, md, csv, json, log…)"
            aria-label="Attach documents"
            disabled={disabled}
          >
            {I.plus}
          </button>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            accept=".txt,.md,.markdown,.csv,.tsv,.json,.log,.xml,.html,.htm,.yaml,.yml,.sql,.ini,.conf,.cfg,text/*"
            onChange={(e) => {
              if (e.target.files) void addFiles(e.target.files);
              e.target.value = '';
            }}
            data-testid="attach-input"
          />
          <span className="text-muted flex-grow-1 text-truncate" style={{ fontSize: 11 }}>
            Enter to send · Shift+Enter new line · paste or drop documents
          </span>
          {busy ? (
            <button
              type="button"
              className="btn btn-dark rounded-circle d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34, padding: 0 }}
              onClick={onStop}
              title="Stop"
              aria-label="Stop"
              data-testid="assistant-stop"
            >
              {I.stop}
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-primary rounded-circle d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34, padding: 0 }}
              disabled={disabled || (!text.trim() && attachments.length === 0)}
              title="Send"
              aria-label="Send"
              data-testid="assistant-send"
            >
              {I.send}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

// ── Main ────────────────────────────────────────────────────────────────

export interface AssistantDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function AssistantDrawer({ open, onClose }: AssistantDrawerProps) {
  const { staff } = useAuth();
  const isPlatformAdmin = staff?.isPlatformAdmin === true;
  const location = useLocation();
  const myName = staff?.displayName || staff?.email || null;
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [busySince, setBusySince] = useState<number | null>(null);
  const [catalogue, setCatalogue] = useState<ReportCatalogueEntry[]>(() => withClientReports(FALLBACK_CATALOGUE));
  const [wikiMe, setWikiMe] = useState<WikiMeLite | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [owner, setOwner] = useState<{ name: string | null; mine: boolean; createdAt: string | null }>({ name: null, mine: true, createdAt: null });
  const [showHistory, setShowHistory] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [prefill, setPrefill] = useState<{ text: string; n: number } | null>(null);
  const pumpMatch = /^\/pumps\/([^/]+)/.exec(location.pathname);
  // Only a plausible pump id goes into the prompt (the URL is user-controlled).
  const pagePump = (() => {
    if (!pumpMatch) return null;
    let id = '';
    try {
      id = decodeURIComponent(pumpMatch[1]!);
    } catch {
      return null;
    }
    return /^[A-Za-z0-9:_-]{1,48}$/.test(id) ? id : null;
  })();
  const [contextOff, setContextOff] = useState<string | null>(null);
  const contextPump = pagePump && contextOff !== pagePump ? pagePump : null;
  const [notice, setNotice] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [geo, setGeo] = useState<Geometry>(() => (typeof window !== 'undefined' ? clampGeometry(loadGeometry()) : loadGeometry()));
  const [dragOver, setDragOver] = useState(false);
  const history = useRef<ChatTurn[]>([]);
  const abort = useRef<AbortController | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const lastAsk = useRef<{ text: string; attachments: Attachment[]; content: string } | null>(null);
  // Conversation generation: bumped on New chat / open / continue, so a late reply or save from an
  // earlier conversation can never write into the current one.
  const gen = useRef(0);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const actionGen = useRef(new Map<string, number>());
  const chatIdRef = useRef<string | null>(null);
  const messagesRef = useRef<UiMessage[]>([]);
  messagesRef.current = messages;
  const readOnly = !owner.mine;

  // Geometry: persist, keep on screen.
  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(geo));
    } catch {
      /* convenience */
    }
  }, [geo]);
  useEffect(() => {
    const onResize = () => setGeo((g) => clampGeometry(g));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getFn<{ reports: ReportCatalogueEntry[] }>('aquastarAdminReport', { catalogue: 1 })
      .then((r) => {
        if (!cancelled && Array.isArray(r.reports) && r.reports.length) setCatalogue(withClientReports(r.reports));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open]);

  // The wiki markup tools are offered only to people with a wiki role (GET /wiki/me).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchWikiMe().then((me) => {
      if (!cancelled) setWikiMe(me && me.can.read ? me : null);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => () => abort.current?.abort(), []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const patch = useCallback((id: string, fn: (m: UiMessage) => UiMessage) => {
    setMessages((ms) => ms.map((m) => (m.id === id ? fn(m) : m)));
  }, []);

  const persist = useCallback(
    async (idNow: string | null) => {
      const ms = messagesRef.current;
      if (!ms.some((m) => m.kind === 'user')) return idNow;
      setSaveState('saving');
      try {
        const r = await saveChat({ id: idNow, title: titleFrom(ms), page: pageName(location.pathname), messages: ms });
        setSaveState('saved');
        return r.id;
      } catch {
        setSaveState('error');
        return idNow;
      }
    },
    [location.pathname],
  );

  /** A person decided on a proposal card: tell the model (next turn) and save the chat. */
  const recordDecision = useCallback(
    (toolResult: string) => {
      history.current = [...history.current, { role: 'user', content: toolResult }];
      const g = gen.current;
      setTimeout(() => {
        saveChain.current = saveChain.current.then(async () => {
          if (gen.current !== g) return;
          const id = await persist(chatIdRef.current);
          if (gen.current === g && id && id !== chatIdRef.current) {
            chatIdRef.current = id;
            setChatId(id);
          }
        });
      }, 0);
    },
    [persist],
  );

  const ask = useCallback(
    async (text: string, atts: Attachment[]) => {
      const q = text.trim();
      if ((!q && atts.length === 0) || busy || readOnly) return;
      setBusy(true);
      setBusySince(Date.now());
      setAttachments([]);
      const myGen = gen.current;
      const ctrl = new AbortController();
      abort.current = ctrl;
      const { content, metas } = composeUserContent(q, atts);
      lastAsk.current = { text: q, attachments: atts, content: content || '(see attachments)' };
      const stale = () => gen.current !== myGen;
      setMessages((ms) => [...ms, { kind: 'user', id: nextId(), text: q, at: nowIso(), by: myName, attachments: metas.length ? metas : undefined }]);
      const system: ChatTurn = {
        role: 'system',
        content: buildSystemPrompt({
          catalogue,
          staffName: myName,
          role: staff?.role ?? null,
          page: pageName(location.pathname),
          stage: STAGE,
          contextPump,
          canCreateIssues: true,
          wiki: wikiMe,
        }),
      };
      const maxRounds = wikiMe ? MAX_WIKI_TOOL_ROUNDS : MAX_TOOL_ROUNDS;
      history.current = [...history.current, { role: 'user', content: content || '(see attachments)' }];
      // "Top issues?" — pump faults or the Issues list? Ask rather than guess (the model is told
      // the same; this catches the plain case without a round trip, whichever model answers).
      try {
        const prev = [...messagesRef.current].reverse().find((m) => m.kind === 'assistant');
        const justAsked = prev?.kind === 'assistant' && !!prev.choices?.length;
        if (atts.length === 0 && !contextPump && !justAsked && needsIssuesClarification(q)) {
          history.current = [...history.current, { role: 'assistant', content: ISSUES_CLARIFY.question }];
          setMessages((ms) => [
            ...ms,
            { kind: 'assistant', id: nextId(), text: ISSUES_CLARIFY.question, at: nowIso(), choices: [...ISSUES_CLARIFY.options] },
          ]);
          return; // finally still runs: busy cleared, chat saved
        }
        for (let round = 0; round <= maxRounds; round++) {
          const aid = nextId();
          setMessages((ms) => [...ms, { kind: 'assistant', id: aid, text: '', at: nowIso(), pending: true }]);
          const res = await streamChatEx(
            [system, ...tailFromUser(history.current, 16)],
            (t) => patch(aid, (m) => ({ ...m, text: visibleText(t) })),
            ctrl.signal,
          );
          if (stale()) return;
          const full = res.text;
          history.current = [...history.current, { role: 'assistant', content: full }];
          const call = round < maxRounds ? parseToolCall(full) : null;
          const shown = visibleText(full);
          const stats = {
            waitMs: res.waitMs,
            genMs: res.genMs,
            totalMs: res.totalMs,
            usage: res.usage,
            model: res.model,
            // Which service answered — stored with the chat; forViewer shows it to platform admins only.
            provider: res.provider,
          };
          if (!call) {
            const unfinished = round === maxRounds && !!parseToolCall(full);
            patch(aid, (m) => ({
              ...m,
              ...stats,
              text: shown || (unfinished ? 'I ran out of steps before I could answer — try asking for one report at a time.' : 'Done.'),
              pending: false,
            }));
            break;
          }
          // Tool turn: drop the (empty) bubble, show the tool's card instead.
          setMessages((ms) => ms.filter((m) => m.id !== aid || shown));
          if (shown) patch(aid, (m) => ({ ...m, ...stats, text: shown, pending: false }));
          if (call.tool === 'ask_user') {
            // A clarifying question: show it with its answers as buttons; the loop ends here.
            const qText = shown ? `${shown}\n\n${call.question}` : call.question;
            setMessages((ms) => [
              ...ms.filter((m) => m.id !== aid),
              { kind: 'assistant', id: aid, text: qText, at: nowIso(), ...stats, choices: call.options.length ? call.options : undefined },
            ]);
            break;
          }
          if (call.tool === 'create_issue') {
            // A proposal only: the person edits and confirms on the card. The loop ends here.
            setMessages((ms) => [
              ...ms,
              { kind: 'action', id: nextId(), at: nowIso(), action: 'create_issue', title: call.title, description: call.description, severity: call.severity, status: 'proposed', by: myName },
            ]);
            break;
          }
          if (call.tool === 'wiki') {
            const wc = call.call;
            if (!wikiMe) {
              history.current = [...history.current, { role: 'user', content: `\`\`\`tool_result\n${JSON.stringify({ ok: false, error: 'Review markups are not available to this user.' })}\n\`\`\`` }];
              continue;
            }
            if (isWikiWrite(wc)) {
              // A proposal only: the person confirms on the card. The loop ends here.
              if (!wikiMe.can.review) {
                setMessages((ms) => [...ms, { kind: 'error', id: nextId(), text: 'You need the reviewer role on the wiki to file issues from markups or complete them.', at: nowIso() }]);
                break;
              }
              setMessages((ms) => [
                ...ms,
                wc.tool === 'create_issues_from_markup'
                  ? { kind: 'wiki-action', id: nextId(), at: nowIso(), action: 'create_issues', ref: wc.ref, version: wc.version, issues: wc.issues, status: 'proposed', by: myName }
                  : { kind: 'wiki-action', id: nextId(), at: nowIso(), action: 'complete', ref: wc.ref, version: wc.version, note: wc.note, status: 'proposed', by: myName },
              ]);
              break;
            }
            const wid = nextId();
            setMessages((ms) => [...ms, { kind: 'report-loading', id: wid, title: 'Review markups', at: nowIso() }]);
            let wikiResult: Record<string, unknown>;
            try {
              let msg: UiMessage;
              if (wc.tool === 'list_review_markups') {
                const data = digestList(await listMarkups(wc.status, wc.page));
                msg = { kind: 'wiki', id: wid, at: nowIso(), tool: wc.tool, status: wc.status, data };
              } else if (wc.tool === 'get_markup') {
                const d = await getMarkup(wc.ref);
                const ver = pickVersion(d, wc.version);
                msg = { kind: 'wiki', id: wid, at: nowIso(), tool: wc.tool, data: digestMarkup(d, wc.version), snapshotPath: ver?.snapshotUrl ?? null };
              } else {
                msg = { kind: 'wiki', id: wid, at: nowIso(), tool: wc.tool, data: digestProposals(wc.ref, wc.version, await draftIssues(wc.ref, wc.version)) };
              }
              if (stale()) return;
              setMessages((xs) => xs.map((m) => (m.id === wid ? msg : m)));
              wikiResult = { ok: true, tool: wc.tool, ...(msg.kind === 'wiki' ? msg.data : {}) };
            } catch (e) {
              const emsg = e instanceof Error ? e.message : 'Could not read the markups';
              setMessages((xs) => xs.map((m) => (m.id === wid ? { kind: 'error', id: wid, text: `Review markups: ${emsg}`, at: nowIso() } : m)));
              wikiResult = { ok: false, tool: wc.tool, error: emsg };
            }
            history.current = [...history.current, { role: 'user', content: `\`\`\`tool_result\n${JSON.stringify(wikiResult)}\n\`\`\`\n${WIKI_RESULT_NUDGE}` }];
            continue;
          }
          const lid = nextId();
          const title = catalogue.find((c) => c.id === call.report)?.title ?? 'Report';
          setMessages((ms) => [...ms, { kind: 'report-loading', id: lid, title, at: nowIso() }]);
          let toolResult: Record<string, unknown>;
          const t0 = Date.now();
          try {
            const args = { days: call.days, hours: call.hours, pump: call.pump };
            const result = await runReport(call.report, args);
            if (stale()) return;
            const ms = Date.now() - t0;
            setMessages((xs) => xs.map((m) => (m.id === lid ? { kind: 'report', id: lid, result, at: nowIso(), ms, args: { report: call.report, ...args } } : m)));
            toolResult = { ok: true, ...reportDigest(result) };
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'The report failed';
            setMessages((xs) => xs.map((m) => (m.id === lid ? { kind: 'error', id: lid, text: `${title}: ${msg}`, at: nowIso() } : m)));
            toolResult = { ok: false, error: msg };
          }
          history.current = [
            ...history.current,
            { role: 'user', content: `\`\`\`tool_result\n${JSON.stringify(toolResult)}\n\`\`\`\n${TOOL_RESULT_NUDGE}` },
          ];
        }
      } catch (e) {
        if (stale()) return;
        // Keep the transcript well-formed for the next turn: no user turn left without a reply.
        if (history.current.at(-1)?.role === 'user') history.current = history.current.slice(0, -1);
        if (ctrl.signal.aborted) {
          setMessages((ms) => [...ms, { kind: 'error', id: nextId(), text: 'Stopped.', at: nowIso() }]);
        } else {
          const msg = e instanceof ChatError || e instanceof Error ? e.message : 'The assistant is unavailable.';
          const status = e instanceof ChatError ? e.status : undefined;
          // Raw text is kept (platform admins diagnose from it); forViewer shows staff a neutral line.
          setMessages((ms) => [...ms.filter((m) => !(m.kind === 'assistant' && m.pending && !m.text)), { kind: 'error', id: nextId(), text: msg, at: nowIso(), ai: true, ...(status ? { status } : {}) }]);
        }
      } finally {
        if (abort.current === ctrl) abort.current = null;
        setBusy(false);
        setBusySince(null);
        if (!stale()) {
          setMessages((ms) => ms.map((m) => (m.kind === 'assistant' && m.pending ? { ...m, pending: false } : m)));
          // Save after React has applied the final message state; saves run one at a time so a
          // second save always carries the id the first one created.
          setTimeout(() => {
            saveChain.current = saveChain.current.then(async () => {
              if (stale()) return;
              const id = await persist(chatIdRef.current);
              if (!stale() && id && id !== chatIdRef.current) {
                chatIdRef.current = id;
                setChatId(id);
              }
            });
          }, 0);
        }
      }
    },
    [busy, catalogue, contextPump, location.pathname, myName, patch, persist, readOnly, staff, wikiMe],
  );
  chatIdRef.current = chatId;

  const newChat = () => {
    gen.current++;
    lastAsk.current = null;
    abort.current?.abort();
    history.current = [];
    setMessages([]);
    setAttachments([]);
    setChatId(null);
    setOwner({ name: null, mine: true, createdAt: null });
    setSaveState('idle');
    setShowHistory(false);
  };

  const openChat = async (id: string) => {
    if (busy) return;
    try {
      const c = await loadChat(id);
      gen.current++;
      lastAsk.current = null;
      abort.current?.abort();
      setMessages(c.messages);
      history.current = turnsFromMessages(c.messages);
      chatIdRef.current = c.id;
      setChatId(c.id);
      setOwner({ name: c.userName, mine: c.mine, createdAt: c.createdAt });
      setAttachments([]);
      setSaveState('idle');
      setShowHistory(false);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Could not open the chat');
    }
  };

  const continueAsNew = () => {
    gen.current++;
    lastAsk.current = null;
    chatIdRef.current = null;
    setChatId(null);
    setOwner({ name: null, mine: true, createdAt: null });
    setSaveState('idle');
  };

  const retry = () => {
    const last = lastAsk.current;
    if (!last || busy) return;
    // Drop everything after the last user turn (and that turn — ask() re-adds it).
    setMessages((ms) => {
      const idx = ms.map((m) => m.kind).lastIndexOf('user');
      return idx >= 0 ? ms.slice(0, idx) : ms;
    });
    // Rewind the model history to before the last *real* user turn — but only if that turn is the
    // question being retried (after an error/Stop it was already removed).
    for (let i = history.current.length - 1; i >= 0; i--) {
      const t = history.current[i]!;
      if (t.role === 'user' && !t.content.startsWith('```tool_result')) {
        if (t.content === last.content) history.current = history.current.slice(0, i);
        break;
      }
    }
    setTimeout(() => void ask(last.text, last.attachments), 0);
  };

  const exportChat = () => {
    const ms = messagesRef.current;
    if (!ms.length) return;
    const svgs: Record<string, string | null> = {};
    for (const m of ms) {
      if (m.kind === 'report') {
        const sel = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(m.id) : m.id.replace(/["\\]/g, '');
        const el = document.querySelector(`[data-report-msg="${sel}"]`) as HTMLElement | null;
        svgs[m.id] = chartSvgFrom(el);
      }
    }
    const title = titleFrom(ms);
    const html = buildChatHtml({
      title,
      messages: forViewer(ms, isPlatformAdmin),
      owner: owner.mine ? myName : owner.name,
      exportedBy: myName,
      createdAt: owner.createdAt,
      chartSvgs: svgs,
      origin: window.location.origin,
    });
    downloadHtml(`assistant-${slugify(title)}-${new Date().toISOString().slice(0, 10)}.html`, html);
  };

  if (!open) return null;

  const narrow = typeof window !== 'undefined' && window.innerWidth < 576;
  const floating = geo.mode === 'floating' && !narrow;
  const frameStyle: React.CSSProperties = floating
    ? { position: 'fixed', left: geo.x, top: geo.y, width: geo.w, height: geo.h, borderRadius: 14, overflow: 'hidden' }
    : { position: 'fixed', top: 0, right: 0, bottom: 0, width: narrow ? '100vw' : Math.min(geo.dockW, window.innerWidth) };

  const lastAssistantId = [...messages].reverse().find((m) => m.kind === 'assistant')?.id;
  // Follow-ups come from the last report in the latest exchange.
  const followUps = (() => {
    const lastUser = messages.map((m) => m.kind).lastIndexOf('user');
    const rep = [...messages.slice(lastUser + 1)].reverse().find((m) => m.kind === 'report') as Extract<UiMessage, { kind: 'report' }> | undefined;
    return rep ? followUpsFor(rep.result.report, rep.result.params ?? {}).slice(0, 3) : [];
  })();
  const title = messages.length ? titleFrom(messages) : '✨ Assistant';

  return (
    <aside
      data-testid="assistant-drawer"
      data-mode={floating ? 'floating' : 'docked'}
      aria-label="Assistant"
      style={{
        ...frameStyle,
        background: 'var(--aqua-surface-muted)',
        boxShadow: floating ? '0 18px 50px rgba(10,20,45,0.35)' : 'var(--aqua-shadow-lg)',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        border: floating ? '1px solid rgba(16,28,66,0.12)' : undefined,
      }}
      onDragOver={(e) => {
        if (readOnly) return;
        if (Array.from(e.dataTransfer.types).includes('Files')) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
      }}
      onDrop={(e) => {
        if (readOnly) return;
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        void (async () => {
          for (const f of files) {
            try {
              const a = await readAttachment(f);
              setAttachments((xs) => [...xs, a]);
            } catch (err) {
              setNotice(err instanceof Error ? err.message : `Could not read ${f.name}`);
            }
          }
        })();
      }}
    >
      {/* Docked: drag the left edge to resize. */}
      {!floating && !narrow && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the assistant"
          title="Drag to resize"
          onPointerDown={(e) => {
            const start = geo.dockW;
            startDrag(e, (dx) => setGeo((g) => clampGeometry({ ...g, dockW: start - dx })));
          }}
          style={{ position: 'absolute', left: -3, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 6, touchAction: 'none' }}
          data-testid="dock-resize"
        />
      )}

      {/* Header — drag to move when floating. */}
      <div
        className="d-flex align-items-center gap-2 px-3"
        style={{
          height: floating ? 52 : 'var(--aqua-topbar-h)',
          flex: '0 0 auto',
          background: 'var(--aqua-navy)',
          color: 'var(--aqua-text-on-dark)',
          cursor: floating ? 'move' : undefined,
          touchAction: floating ? 'none' : undefined,
        }}
        onPointerDown={(e) => {
          if (!floating) return;
          const sx = geo.x;
          const sy = geo.y;
          startDrag(e, (dx, dy) => setGeo((g) => clampGeometry({ ...g, x: sx + dx, y: sy + dy })));
        }}
        onDoubleClick={() => setGeo((g) => ({ ...g, mode: g.mode === 'floating' ? 'docked' : 'floating' }))}
      >
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-bold text-truncate" title={title}>
            {title}
          </div>
          <div className="text-truncate" style={{ fontSize: 11, color: 'var(--aqua-text-on-dark-muted)' }}>
            {readOnly
              ? `${owner.name ?? 'Someone'}'s chat · read-only`
              : saveState === 'saving'
                ? 'Saving…'
                : saveState === 'saved'
                  ? 'Saved to history'
                  : saveState === 'error'
                    ? 'Not saved — history unavailable'
                    : 'Ask about your fleet — reports can be pinned to the dashboard'}
          </div>
        </div>
        <HeadButton label="How to use + example prompts" onClick={() => { setShowHelp((v) => !v); setShowHistory(false); }} active={showHelp} testId="assistant-help-btn">
          {I.info}
        </HeadButton>
        <HeadButton label={busy ? 'Chat history (available when the reply finishes)' : 'Chat history'} onClick={() => { setShowHistory((v) => !v); setShowHelp(false); }} active={showHistory} disabled={busy} testId="assistant-history-btn">
          {I.history}
        </HeadButton>
        <HeadButton label="New chat" onClick={newChat} disabled={busy} testId="assistant-new">
          {I.newChat}
        </HeadButton>
        <HeadButton label="Export chat as HTML" onClick={exportChat} disabled={messages.length === 0} testId="assistant-export">
          {I.download}
        </HeadButton>
        {!narrow && (
          <HeadButton
            label={floating ? 'Dock to the right' : 'Float window'}
            onClick={() => setGeo((g) => clampGeometry({ ...g, mode: g.mode === 'floating' ? 'docked' : 'floating' }))}
            testId="assistant-dock-toggle"
          >
            {floating ? I.dock : I.float}
          </HeadButton>
        )}
        <HeadButton label="Close assistant" onClick={onClose} testId="assistant-close">
          {I.close}
        </HeadButton>
      </div>

      <div className="position-relative flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
        {showHistory && (
          <HistoryPanel
            activeId={chatId}
            onOpen={(id) => void openChat(id)}
            onClose={() => setShowHistory(false)}
            onDeleted={(id) => {
              if (id === chatId) newChat();
            }}
          />
        )}

        {showHelp && (
          <HelpPanel
            onClose={() => setShowHelp(false)}
            onUse={(t) => {
              setShowHelp(false);
              setPrefill({ text: t, n: Date.now() });
            }}
          />
        )}

        {readOnly && (
          <div className="alert alert-info small m-2 mb-0 py-2 d-flex align-items-center gap-2">
            <span className="flex-grow-1">
              Viewing <b>{owner.name ?? 'another staff member'}</b>&apos;s conversation.
            </span>
            <button type="button" className="btn btn-sm btn-primary" onClick={continueAsNew}>
              Continue in a new chat
            </button>
          </div>
        )}

        <div ref={scroller} className="flex-grow-1 px-3 py-3" style={{ overflowY: 'auto', minHeight: 0 }} aria-live="polite">
          {messages.length === 0 && (
            <div>
              <p className="text-muted small mb-2">
                Try one of these — or <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setShowHelp(true)}>see all example prompts</button>:
              </p>
              <div className="d-flex flex-column gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="btn btn-sm btn-outline-primary text-start" onClick={() => void ask(s, [])} disabled={busy}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {forViewer(messages, isPlatformAdmin).map((m) => {
            if (m.kind === 'user') {
              return (
                <div key={m.id} className="d-flex flex-column align-items-end my-3" data-testid="user-msg">
                  <div className="px-3 py-2 small" style={{ background: 'var(--aqua-primary)', color: '#fff', borderRadius: 16, maxWidth: '85%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {m.text}
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-1 d-flex flex-wrap gap-1">
                        {m.attachments.map((a, i) => (
                          <span key={i} className="badge fw-normal" style={{ background: 'rgba(255,255,255,0.22)' }} title={a.truncated ? 'Only the first part was sent' : undefined}>
                            📄 {a.name}
                            {a.truncated ? ' (cut)' : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                    {m.by && (!owner.mine || m.by !== myName) ? `${m.by} · ` : ''}
                    {fmtClock(m.at)}
                  </div>
                </div>
              );
            }
            if (m.kind === 'assistant') {
              const stats = statsLine(m);
              return (
                <div key={m.id} className="my-3" data-testid="assistant-msg">
                  <div className="small" style={{ lineHeight: 1.55, wordBreak: 'break-word' }} data-testid="assistant-text">
                    {m.text ? (
                      <Markdown text={m.text} />
                    ) : m.pending ? (
                      <span className="text-muted d-inline-flex align-items-center gap-2">
                        <span className="spinner-grow spinner-grow-sm" aria-hidden /> Thinking… {busySince && <Elapsed since={busySince} />}
                      </span>
                    ) : null}
                  </div>
                  {m.choices && m.choices.length > 0 && m.id === lastAssistantId && !busy && !readOnly && (
                    <div className="d-flex flex-wrap gap-1 mt-2" data-testid="clarify-choices">
                      {m.choices.map((c) => (
                        <button key={c} type="button" className="btn btn-sm btn-outline-primary rounded-pill" style={{ fontSize: 12 }} onClick={() => void ask(c, [])}>
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                  {m.id === lastAssistantId && !busy && !readOnly && followUps.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2" data-testid="follow-ups">
                      {followUps.map((f) => (
                        <button key={f} type="button" className="btn btn-sm btn-light border rounded-pill" style={{ fontSize: 12 }} onClick={() => void ask(f, [])}>
                          {f}
                        </button>
                      ))}
                    </div>
                  )}
                  {!m.pending && m.text && (
                    <div className="d-flex flex-wrap align-items-center gap-2 text-muted" style={{ fontSize: 11 }} data-testid="assistant-stats">
                      <span>{fmtClock(m.at)}</span>
                      {stats.map((s, i) => (
                        <span key={i}>· {s}</span>
                      ))}
                      <span className="ms-auto d-inline-flex gap-3">
                        <CopyButton text={m.text} />
                        {m.id === lastAssistantId && !readOnly && lastAsk.current && !busy && (
                          <button type="button" className="btn btn-link btn-sm p-0 text-muted text-decoration-none d-inline-flex align-items-center gap-1" style={{ fontSize: 11 }} onClick={retry} title="Ask again">
                            {I.retry} Retry
                          </button>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              );
            }
            if (m.kind === 'report-loading') {
              return (
                <div key={m.id} className="small text-muted my-2">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Running “{m.title}”…
                </div>
              );
            }
            if (m.kind === 'report') return <ReportBubble key={m.id} msg={m} readOnly={readOnly} />;
            if (m.kind === 'action') {
              return (
                <ActionCard
                  key={m.id}
                  msg={m}
                  readOnly={readOnly}
                  onUpdate={(p) => {
                    // A Create that finishes after the person switched chats must not touch the new one.
                    if (actionGen.current.get(m.id) !== undefined && actionGen.current.get(m.id) !== gen.current) return;
                    actionGen.current.set(m.id, gen.current);
                    patch(m.id, (x) => ({ ...x, ...p }) as UiMessage);
                    if (p.status === 'created' || p.status === 'cancelled') {
                      recordDecision(`\`\`\`tool_result\n${JSON.stringify({ tool: 'create_issue', status: p.status, issueNumber: p.issueNumber ?? null })}\n\`\`\``);
                    }
                  }}
                />
              );
            }
            if (m.kind === 'wiki') return <WikiResultCard key={m.id} msg={m} />;
            if (m.kind === 'wiki-action') {
              return (
                <WikiActionCard
                  key={m.id}
                  msg={m}
                  readOnly={readOnly}
                  onUpdate={(p) => {
                    // A confirm that finishes after the person switched chats must not touch the new one.
                    if (actionGen.current.get(m.id) !== undefined && actionGen.current.get(m.id) !== gen.current) return;
                    actionGen.current.set(m.id, gen.current);
                    patch(m.id, (x) => ({ ...x, ...p }) as UiMessage);
                    if (p.status === 'done' || p.status === 'cancelled') recordDecision(wikiActionResult({ ...m, ...p }));
                  }}
                />
              );
            }
            return (
              <div key={m.id} className="alert alert-warning small my-2 py-2">
                {m.text}
              </div>
            );
          })}
        </div>

        {notice && (
          <div className="alert alert-warning small mx-3 mb-1 py-2" role="status">
            {notice}
          </div>
        )}
        {dragOver && (
          <div className="position-absolute d-flex align-items-center justify-content-center" style={{ inset: 8, border: '2px dashed var(--aqua-primary)', borderRadius: 12, background: 'rgba(1,157,220,0.06)', zIndex: 4, pointerEvents: 'none' }}>
            <span className="fw-semibold" style={{ color: 'var(--aqua-primary)' }}>
              Drop documents to attach
            </span>
          </div>
        )}

        <Composer
          busy={busy}
          disabled={readOnly}
          onSend={(t) => void ask(t, attachments)}
          onStop={() => abort.current?.abort()}
          attachments={attachments}
          setAttachments={(fn) => setAttachments(fn)}
          onError={(msg) => setNotice(msg)}
          prefill={prefill}
          contextLabel={contextPump ? `This pump: ${contextPump}` : null}
          onClearContext={() => setContextOff(pagePump)}
        />
      </div>

      {/* Floating: resize from the right edge, bottom edge and corner. */}
      {floating && (
        <>
          <div
            onPointerDown={(e) => {
              const sw = geo.w;
              startDrag(e, (dx) => setGeo((g) => clampGeometry({ ...g, w: sw + dx })));
            }}
            style={{ position: 'absolute', right: 0, top: 52, bottom: 14, width: 6, cursor: 'ew-resize' }}
            aria-hidden
          />
          <div
            onPointerDown={(e) => {
              const sh = geo.h;
              startDrag(e, (_dx, dy) => setGeo((g) => clampGeometry({ ...g, h: sh + dy })));
            }}
            style={{ position: 'absolute', left: 0, right: 14, bottom: 0, height: 6, cursor: 'ns-resize' }}
            aria-hidden
          />
          <div
            onPointerDown={(e) => {
              const sw = geo.w;
              const sh = geo.h;
              startDrag(e, (dx, dy) => setGeo((g) => clampGeometry({ ...g, w: sw + dx, h: sh + dy })));
            }}
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              background: 'linear-gradient(135deg, transparent 50%, rgba(16,28,66,0.25) 50%, rgba(16,28,66,0.25) 60%, transparent 60%, transparent 70%, rgba(16,28,66,0.25) 70%, rgba(16,28,66,0.25) 80%, transparent 80%)',
            }}
            title="Drag to resize"
            data-testid="float-resize"
          />
        </>
      )}
    </aside>
  );
}
